<script setup lang="ts">
import { useGetAdminAlgorithmVersion, useGetAdminCompany, useListAdminAlgorithmVersions } from '@swipe4work/api-orval'
import type { Feature, FeatureImportance } from '@swipe4work/models-orval'
import { useMatchingAdapter } from '@/shared/adapters/matching/useMatchingAdapter'
import CompanySelect from '@/shared/components/form/CompanySelect.vue'
import {
  type ConfigurableFeature,
  IMPORTANCE_OPTIONS,
  POOL_OPTIONS,
  useMatchSimulator,
} from '../composables/useMatchSimulator'
import type { MatchScoreFeatureBreakdown } from '@/features/matching/data-tables/types'
import ScoreCurveSimulator from '@/features/matching/components/score-curve/ScoreCurveSimulator.vue'

definePage({
  path: '/matching/score-simulator',
  name: '/matching/ScoreSimulator',
  meta: {
    title: 'Score Simulator',
  },
})

// --- Algorithm version selector ---

const { data: versionsData, isPending: isVersionsPending } = useListAdminAlgorithmVersions({
  query: { staleTime: 5 * 60 * 1000 },
})

const algorithmVersions = computed(() => versionsData.value?.algorithmVersions ?? [])

const selectedVersionId = ref<number | null>(null)

watch(algorithmVersions, (versions) => {
  if (selectedVersionId.value !== null || versions.length === 0) return

  const standardActive = versions.find(
    v => v.isActive && !v.version.includes('simple') && !v.version.includes('lead'),
  )
  const anyActive = versions.find(v => v.isActive)

  selectedVersionId.value = standardActive?.id ?? anyActive?.id ?? versions[0]?.id ?? null
}, { immediate: true })

const { data: versionData, isPending: isVersionPending } = useGetAdminAlgorithmVersion(
  computed(() => selectedVersionId.value ?? 0),
  {
    query: {
      enabled: computed(() => selectedVersionId.value !== null),
      staleTime: 5 * 60 * 1000,
    },
  },
)

const version = computed(() => versionData.value?.algorithmVersion ?? null)
const isPending = computed(() => isVersionsPending.value || (selectedVersionId.value !== null && isVersionPending.value))

const {
  candidatePoolLabel,
  candidatePoolThresholdEffect,
  featureLabel,
  featureColor,
  featureImportanceLabel,
} = useMatchingAdapter()

const {
  importances,
  candidatePool,
  rawScorePercents,
  customWeights,
  hardSkillsThresholds,
  hardSkillsMatchedSkillCount,
  featureRows,
  total,
  defaultTotal,
  adjustedThreshold,
  isVisible,
  resetDefaults,
  isCurveDriven,
} = useMatchSimulator(version)

// --- Company configuration loader ---

const selectedCompanyId = ref<number | null>(null)

const { data: companyData, isFetching: isLoadingCompany } = useGetAdminCompany(
  computed(() => selectedCompanyId.value ?? 0),
  {
    query: {
      enabled: computed(() => selectedCompanyId.value !== null),
      staleTime: 5 * 60 * 1000,
    },
  },
)

watch(companyData, (data) => {
  if (!data?.matchingConfiguration) return
  const config = data.matchingConfiguration.configuration

  importances.value.HardSkills = config.hardSkillsImportance
  importances.value.YearsExperience = config.workExperienceImportance
  importances.value.EducationLevel = config.educationImportance
  importances.value.PersonalCharacteristics = config.personalityImportance
})

const companyHasNoConfig = computed(() =>
  selectedCompanyId.value !== null && !isLoadingCompany.value && !companyData.value?.matchingConfiguration,
)

function resetAll() {
  resetDefaults()
  selectedCompanyId.value = null
}

// --- Feature weights editor ---

const customWeightTotal = computed(() =>
  version.value?.featureWeights.reduce((sum, fw) => sum + (customWeights.value[fw.feature] ?? fw.weight), 0) ?? 0,
)

const customWeightRows = computed(() =>
  version.value?.featureWeights.map((fw) => {
    const currentWeight = customWeights.value[fw.feature] ?? fw.weight

    return {
      feature: fw.feature,
      defaultWeight: fw.weight,
      currentWeight,
      pct: customWeightTotal.value > 0 ? Math.round(currentWeight / customWeightTotal.value * 100) : 0,
    }
  }) ?? [],
)

const featureWeightChartData = computed(() => ({
  labels: customWeightRows.value.map(r => featureLabel(r.feature)),
  datasets: [
    {
      data: customWeightRows.value.map(r => r.currentWeight),
      backgroundColor: customWeightRows.value.map(r => featureColor(r.feature)),
      borderWidth: 2,
      borderColor: '#ffffff',
      hoverOffset: 4,
    },
  ],
}))

const isWeightUnbalanced = computed(() => Math.abs(customWeightTotal.value - 1) > 0.001)

const centerTextPlugin = {
  id: 'centerText',
  afterDraw(chart: any) {
    const opts = chart.config?.options?.plugins?.centerText
    if (!opts?.text) return
    const { ctx, chartArea } = chart
    if (!chartArea) return
    ctx.save()
    ctx.font = 'bold 13px Inter, system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = opts.color ?? '#6b7280'
    ctx.fillText(opts.text, (chartArea.left + chartArea.right) / 2, (chartArea.top + chartArea.bottom) / 2)
    ctx.restore()
  },
}

const featureWeightChartOptions = computed(() => ({
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx: {
          label: string;
          raw: number
        }) => ` ${ctx.label}: ${ctx.raw}`,
      },
    },
    centerText: {
      text: `${Math.round(customWeightTotal.value * 100)}%`,
      color: isWeightUnbalanced.value ? '#f59e0b' : '#6b7280',
    },
  } as any,
  cutout: '62%',
  responsive: true,
  maintainAspectRatio: false,
}))

function setCustomWeight(feature: Feature, value: number | null) {
  customWeights.value[feature] = value ?? 0
}

function stepCustomWeight(feature: Feature, direction: 1 | -1) {
  const defaultWeight = version.value?.featureWeights.find(fw => fw.feature === feature)?.weight ?? 0
  const current = customWeights.value[feature] ?? defaultWeight
  const next = Math.round((current + direction * 0.05) * 10000) / 10000

  customWeights.value[feature] = Math.min(1, Math.max(0, next))
}

// --- Feature-specific raw score options ---

const HARD_SKILLS_COUNT_OPTIONS = [
  0,
  1,
  2,
  3,
  4,
  5,
]

interface ScoreOption {
  label: string;
  value: number
}

const BINARY_SCORE_OPTIONS: ScoreOption[] = [
  {
    label: 'Geen match',
    value: 0,
  },
  {
    label: 'Match',
    value: 100,
  },
]

/*
 * EducationLevelDifference: closestPreferred - userLevel
 * ≤0 → 1.0 | 1 → 0.5 | 2 → 0.0 | 3 → −1.0
 */
const EDUCATION_LEVEL_OPTIONS: ScoreOption[] = [
  {
    label: 'Geen verschil',
    value: 100,
  },
  {
    label: '1 niveau verschil',
    value: 50,
  },
  {
    label: '2 niveaus verschil',
    value: 0,
  },
  {
    label: '3 niveaus verschil',
    value: -100,
  },
]

/*
 * Number of personality color-traits matching the job preference
 * 0 → 0 | 1 → 0.5 | 2 → 0.75 | ≥3 → 1.0
 */
const PERSONAL_CHARACTERISTICS_OPTIONS: ScoreOption[] = [
  {
    label: '0',
    value: 0,
  },
  {
    label: '1',
    value: 50,
  },
  {
    label: '2',
    value: 75,
  },
  {
    label: '3+',
    value: 100,
  },
]

// --- Breakdown table data ---

const breakdownFeatures = computed((): MatchScoreFeatureBreakdown[] =>
  featureRows.value.map(row => ({
    feature: row.feature,
    score: row.rawScorePercent / 100,
    defaultWeight: row.defaultWeight,
    weight: row.normalizedWeight,
    weightedScore: row.weightedScore,
    importance: row.importance,
  })),
)
</script>

<template>
  <div class="space-y-5">
    <!-- Header: left = selectors, right = score -->
    <Card>
      <template #content>
        <div class="flex flex-col gap-5 lg:flex-row lg:items-start">
          <!-- Left: version + company config stacked -->
          <div class="flex shrink-0 flex-col gap-4 lg:w-72">
            <div class="flex items-center justify-between">
              <span class="text-sm text-surface-700 font-semibold">Simulator</span>
              <Button
                v-if="version"
                label="Reset"
                icon="fa-regular fa-rotate-left"
                severity="secondary"
                outlined
                size="small"
                @click="resetAll"
              />
            </div>

            <!-- Version selector -->
            <div class="flex flex-col gap-2">
              <div class="flex flex-col gap-0.5">
                <span class="text-sm font-semibold">Algoritme versie</span>
                <span class="text-xs text-surface-400">Versie om te simuleren</span>
              </div>
              <div class="flex items-center gap-2">
                <Select
                  v-model="selectedVersionId"
                  :options="algorithmVersions"
                  option-label="version"
                  option-value="id"
                  placeholder="Selecteer een versie..."
                  :loading="isVersionsPending"
                  class="flex-1"
                >
                  <template #option="{ option }">
                    <div class="flex items-center gap-2">
                      <span>{{ option.version }}</span>
                      <Tag
                        v-if="option.isActive"
                        value="Actief"
                        severity="success"
                        class="text-xs"
                      />
                    </div>
                  </template>
                </Select>
                <Tag
                  v-if="version?.isActive"
                  value="Actief"
                  severity="success"
                />
              </div>
            </div>

            <!-- Company config -->
            <div class="flex flex-col gap-2">
              <div class="flex flex-col gap-0.5">
                <span class="text-sm font-semibold">Bedrijfsconfiguratie</span>
                <span class="text-xs text-surface-400">Vult het belang automatisch in</span>
              </div>
              <CompanySelect
                v-model="selectedCompanyId"
                placeholder="Selecteer een bedrijf..."
                show-clear
                filter-placeholder="Zoek bedrijf"
                :loading="isLoadingCompany"
                size="small"
              />
              <Message
                v-if="companyHasNoConfig"
                severity="secondary"
                class="text-xs"
              >
                Geen aangepaste configuratie — standaard wordt gebruikt.
              </Message>
            </div>
          </div>

          <div class="hidden w-px self-stretch bg-surface-200 lg:block" />

          <!-- Right: reset + breakdown table -->
          <div class="flex flex-1 flex-col gap-3">
            <template v-if="isPending">
              <Skeleton height="4rem" />
              <Skeleton height="2rem" />
            </template>
            <template v-else-if="version">
              <MatchScoreFeatureBreakdownTable
                :features="breakdownFeatures"
                :total="total"
                :default-total="defaultTotal"
                :is-visible="isVisible"
                :threshold="adjustedThreshold"
              />
            </template>
            <div v-else class="text-sm text-surface-400">
              Selecteer een versie om de simulator te starten.
            </div>
          </div>
        </div>
      </template>
    </Card>

    <div v-if="isPending" class="space-y-3">
      <Skeleton v-for="n in 4" :key="n" height="5rem" />
    </div>

    <template v-else-if="version">
      <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <!-- Left: one card per raw score feature -->
        <div class="flex flex-col gap-3">
          <Card
            v-for="row in featureRows"
            :key="row.feature"
          >
            <template #content>
              <div class="flex flex-col gap-2">
                <div class="flex items-center gap-2">
                  <span
                    class="size-2 shrink-0 rounded-full"
                    :style="{ backgroundColor: featureColor(row.feature) }"
                  />
                  <span class="text-sm font-semibold">{{ featureLabel(row.feature) }}</span>
                  <span class="ml-auto text-xs text-surface-400 font-mono">{{ row.rawScorePercent }}%</span>
                </div>
                <template v-if="isCurveDriven(row.feature)">
                  <SelectButton
                    v-model="hardSkillsMatchedSkillCount"
                    :options="HARD_SKILLS_COUNT_OPTIONS"
                    size="small"
                    class="w-full"
                  />
                  <ScoreCurveSimulator
                    v-model="hardSkillsThresholds"
                    input-label="Matchende skills"
                    score-label="Ruwe hard skills score"
                  />
                </template>
                <SelectButton
                  v-else-if="row.feature === 'YearsExperience' || row.feature === 'JobFunction'"
                  :model-value="row.rawScorePercent"
                  :options="BINARY_SCORE_OPTIONS"
                  option-label="label"
                  option-value="value"
                  size="small"
                  class="w-full"
                  @update:model-value="rawScorePercents[row.feature] = $event"
                />
                <SelectButton
                  v-else-if="row.feature === 'EducationLevel'"
                  :model-value="row.rawScorePercent"
                  :options="EDUCATION_LEVEL_OPTIONS"
                  option-label="label"
                  option-value="value"
                  size="small"
                  class="w-full"
                  @update:model-value="rawScorePercents[row.feature] = $event"
                />
                <SelectButton
                  v-else-if="row.feature === 'PersonalCharacteristics'"
                  :model-value="row.rawScorePercent"
                  :options="PERSONAL_CHARACTERISTICS_OPTIONS"
                  option-label="label"
                  option-value="value"
                  size="small"
                  class="w-full"
                  @update:model-value="rawScorePercents[row.feature] = $event"
                />
                <template v-else>
                  <Slider
                    :model-value="row.rawScorePercent"
                    :min="0"
                    :max="100"
                    :step="5"
                    class="w-full"
                    @update:model-value="rawScorePercents[row.feature] = Array.isArray($event) ? $event[0] : $event"
                  />
                </template>
              </div>
            </template>
          </Card>
        </div>

        <!-- Right: feature weights + merged pool & importance -->
        <div class="flex flex-col gap-5">
          <!-- Feature weights editor -->
          <Card>
            <template #content>
              <div class="flex flex-col gap-3">
                <div class="flex flex-col gap-0.5">
                  <span class="text-sm font-semibold">Feature gewichten</span>
                  <span class="text-xs text-surface-400">Pas de basisgewichten aan</span>
                </div>
                <div class="flex items-start gap-4">
                  <div class="h-36 w-36 shrink-0">
                    <Chart
                      type="doughnut"
                      :data="featureWeightChartData"
                      :options="featureWeightChartOptions"
                      :plugins="[centerTextPlugin]"
                      class="h-full w-full"
                    />
                  </div>
                  <div class="min-w-0 flex-1">
                    <div
                      v-for="row in customWeightRows"
                      :key="row.feature"
                      class="flex items-center gap-2 border-b border-surface-100 py-1.5 last:border-0"
                    >
                      <span
                        class="size-2 shrink-0 rounded-full"
                        :style="{ backgroundColor: featureColor(row.feature) }"
                      />
                      <span class="min-w-0 flex-1 truncate text-xs text-surface-600">{{ featureLabel(row.feature) }}</span>
                      <div class="flex shrink-0 items-center">
                        <Button
                          icon="fa-regular fa-chevron-down"
                          size="small"
                          severity="secondary"
                          text
                          :disabled="row.currentWeight <= 0"
                          @click="stepCustomWeight(row.feature as Feature, -1)"
                        />
                        <div class="w-14">
                          <InputNumber
                            :model-value="row.currentWeight"
                            :min="0"
                            :max="1"
                            :max-fraction-digits="4"
                            size="small"
                            fluid
                            @update:model-value="setCustomWeight(row.feature as Feature, $event)"
                          />
                        </div>
                        <Button
                          icon="fa-regular fa-chevron-up"
                          size="small"
                          severity="secondary"
                          text
                          :disabled="row.currentWeight >= 1"
                          @click="stepCustomWeight(row.feature as Feature, 1)"
                        />
                      </div>
                      <span class="w-8 shrink-0 text-right text-xs text-surface-400 tabular-nums">{{ row.pct }}%</span>
                    </div>
                  </div>
                </div>
                <Message
                  v-if="isWeightUnbalanced"
                  severity="warn"
                  class="text-xs"
                >
                  De verdeling is niet 100% — gewichten zouden altijd optellen tot 1.
                </Message>
              </div>
            </template>
          </Card>

          <!-- Kandidatenpool + Bedrijfsconfiguratie -->
          <Card>
            <template #content>
              <div class="flex flex-col gap-5">
                <!-- Kandidatenpool -->
                <div class="flex flex-col gap-3">
                  <div class="flex flex-col gap-0.5">
                    <span class="text-sm font-semibold">Kandidatenpool</span>
                    <span class="text-xs text-surface-400">Bepaalt de drempelaanpassing</span>
                  </div>
                  <SelectButton
                    v-model="candidatePool"
                    :options="POOL_OPTIONS"
                    :option-label="(opt: string) => candidatePoolLabel(opt)"
                    size="small"
                  />
                  <div class="flex items-center gap-2">
                    <Tag
                      :value="candidatePoolThresholdEffect(candidatePool).delta"
                      :severity="candidatePoolThresholdEffect(candidatePool).severity"
                    />
                    <span class="text-xs text-surface-500">{{ candidatePoolThresholdEffect(candidatePool).label }}</span>
                  </div>
                </div>

                <div class="border-t border-surface-100" />

                <!-- Bedrijfsconfiguratie (importance per feature) -->
                <div class="flex flex-col gap-3">
                  <div class="flex flex-col gap-0.5">
                    <span class="text-sm font-semibold">Bedrijfsconfiguratie</span>
                    <span class="text-xs text-surface-400">Belang per factor</span>
                  </div>
                  <div class="flex flex-col">
                    <div
                      v-for="row in featureRows"
                      :key="row.feature"
                      class="flex items-center gap-3 border-b border-surface-100 py-2 last:border-0"
                    >
                      <span
                        class="size-2 shrink-0 rounded-full"
                        :style="{ backgroundColor: featureColor(row.feature) }"
                      />
                      <span class="w-36 shrink-0 truncate text-xs text-surface-600">{{ featureLabel(row.feature) }}</span>
                      <SelectButton
                        v-if="row.configurable"
                        :model-value="importances[row.feature as ConfigurableFeature]"
                        :options="IMPORTANCE_OPTIONS"
                        :option-label="(opt: FeatureImportance) => featureImportanceLabel(opt)"
                        size="small"
                        class="flex-1"
                        @update:model-value="importances[row.feature as ConfigurableFeature] = $event"
                      />
                      <div v-else class="flex items-center gap-1.5 text-xs text-surface-400">
                        <i class="fa-regular fa-lock" />
                        <span>Niet instelbaar</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </Card>
        </div>
      </div>
    </template>
  </div>
</template>
